import type { DropResult } from "react-beautiful-dnd";
import {
    type CodeWorkflowType,
    type CompoundCondition,
    type ConditionClause,
    type Question,
    type SpanWorkflowQuestionScope,
    type WorkflowQuestion,
    type WorkflowTrigger,
    type WorkflowTriggerPhase,
    InclusionMode,
    WorkflowTriggerAction,
} from "../../../../types/rubric";
import type { AnswerValue } from "../../../../types/answerSet";

export type WorkflowQuestionWithClientId = WorkflowQuestion & {
    clientId: string;
}

export const isSpanQuestionType = (questionType: string): boolean =>
    questionType === "SPAN" || questionType === "SPANS";

export const questionCardPalette = {
    background: "#F7FDFF",
    header: "#E9F3FF",
    border: "#BBDCFB",
    triggerBackground: "#F7FDFF",
};

export interface QuestionsSectionProps {
    questions: WorkflowQuestionWithClientId[];
    validTargetQuestions?: WorkflowQuestionWithClientId[];
    allQuestions?: WorkflowQuestionWithClientId[];
    onDragEnd: (result: DropResult) => void;
    collapsedSections: Record<string, boolean>;
    onToggleSection: (questionKey: string) => void;
    onRemoveQuestion: (clientId: string) => void;
    onAddTrigger: (clientId: string) => void;
    onAddShowWhen?: (clientId: string) => void;
    onRemoveTrigger: (clientId: string, triggerIndex: number) => void;
    onTriggerAnswersChange: (clientId: string, triggerIndex: number, values: AnswerValue[]) => void;
    onTriggerTargetChange: (clientId: string, triggerIndex: number, targetId: number | undefined) => void;
    onTriggerActionChange: (clientId: string, triggerIndex: number, action: WorkflowTriggerAction) => void;
    onTriggerToAnswersChange?: (clientId: string, triggerIndex: number, answerIds: number[]) => void;
    onConditionClauseChange?: (clientId: string, triggerIndex: number, clauseIndex: number, field: keyof ConditionClause, value: number | number[]) => void;
    onAddConditionClause?: (clientId: string, triggerIndex: number) => void;
    onRemoveConditionClause?: (clientId: string, triggerIndex: number, clauseIndex: number) => void;
    onOperatorChange?: (clientId: string, triggerIndex: number, operator: CompoundCondition["operator"]) => void;
    questionsAvailableToAdd?: Question[];
    onAddQuestion: (questionId: number) => void;
    showAddQuestion?: boolean;
    droppableId: string;
    onTriggerDragEnd?: (questionClientId: string, sourceIndex: number, destinationIndex: number) => void;
    onExpressionChange?: (clientId: string, expression: string) => void;
    onSpanScopeChange?: (clientId: string, spanScope: SpanWorkflowQuestionScope, spanScopeQuestionId?: number | null) => void;
    /**
     * Full pool of system + workflow questions known to the workflow editor
     * (i.e. props.questions in CodeWorkflowsPanel). Used to resolve a
     * trigger reference to a question that's been removed from the
     * workflow but still exists in the system, so we can show its name
     * with a "removed from workflow" suffix instead of leaking the id.
     */
    globalQuestionPool?: Question[];
    /**
     * Cross-workflow target questions to surface in the trigger Target
     * dropdown for actions that support cross-workflow targets. Today that
     * is only AUTOFILL, but the action capability is centralized so future
     * trigger types can opt in without changing the picker plumbing.
     *
     * Each entry's `question.name` should already be prefixed with its
     * source workflow name so the dropdown disambiguates (e.g. "Procedure
     * Workflow: Was X done?"). Empty/undefined keeps the existing host-only
     * behaviour.
     *
     * SPAN workflows must be excluded by the parent before passing through
     * to this prop. Unsupported actions remain host-only regardless of this
     * prop.
     */
    crossWorkflowTargetQuestions?: WorkflowQuestionWithClientId[];
    /** Host workflow's codeType; SPAN widens the trigger from-picker for cross-workflow source actions. */
    hostCodeType?: CodeWorkflowType;
    /**
     * Cross-workflow source questions for the trigger from-question
     * picker, pre-segregated by codeType. Each question.name should be
     * prefixed with its source workflow name so the picker disambiguates
     * same-named questions.
     */
    crossWorkflowSourceQuestions?: CrossWorkflowSourceExtension;
}

export const isAnswerActiveForQuestion = (question: Question, answer: AnswerValue): boolean => {
    if (answer.activeForQuestion != null) {
        return answer.activeForQuestion;
    }

    if (answer.id == null || !question.inclusionMode || !Array.isArray(question.selectedAnswerIds)) {
        return true;
    }

    if (question.inclusionMode === InclusionMode.INCLUDE) {
        return question.selectedAnswerIds.includes(answer.id);
    }
    return !question.selectedAnswerIds.includes(answer.id);
};

export const getActiveAnswers = (question?: Question): AnswerValue[] => {
    if (!question || !question.answers) {
        return [];
    }
    return question.answers.filter(
        (answer) => answer.active !== false && isAnswerActiveForQuestion(question, answer)
    );
};

export const getAnswersIncludingSelected = (
    question: Question | undefined,
    selectedAnswerIds: number[] = [],
): AnswerValue[] => {
    const activeAnswers = getActiveAnswers(question);
    const activeIds = new Set(activeAnswers.map((answer) => answer.id).filter((id): id is number => id != null));
    const staleAnswers = question?.answers?.filter(
        (answer) => answer.id != null && selectedAnswerIds.includes(answer.id) && !activeIds.has(answer.id)
    ) ?? [];

    return [...activeAnswers, ...staleAnswers];
};

export const isInactiveAnswerOption = (question: Question | undefined, answer: AnswerValue): boolean =>
    question != null && (answer.active === false || !isAnswerActiveForQuestion(question, answer));

export const getAnswerOptionLabel = (question: Question | undefined, answer: AnswerValue): string => {
    const label = answer.name ?? answer.displayText ?? "";
    return isInactiveAnswerOption(question, answer) ? `${label} (inactive)` : label;
};

export const getTriggerPhase = (trigger: WorkflowTrigger): WorkflowTriggerPhase =>
    trigger.triggerPhase ?? "POST";

export const isShowWhenTrigger = (trigger: WorkflowTrigger): boolean =>
    getTriggerPhase(trigger) === "PRE" && trigger.action === WorkflowTriggerAction.SHOW_QUESTION;

/** Cross-workflow source extension input, pre-segregated by codeType. Empty/undefined keeps the legacy ungrouped picker. */
export type CrossWorkflowSourceExtension = {
    codeWorkflows?: WorkflowQuestionWithClientId[];
    caseWorkflows?: WorkflowQuestionWithClientId[];
};

export type TriggerSectionRenderProps = Pick<
    QuestionsSectionProps,
    | "questions"
    | "validTargetQuestions"
    | "allQuestions"
    | "globalQuestionPool"
    | "crossWorkflowTargetQuestions"
    | "crossWorkflowSourceQuestions"
    | "hostCodeType"
    | "onRemoveTrigger"
    | "onTriggerAnswersChange"
    | "onTriggerTargetChange"
    | "onTriggerActionChange"
    | "onTriggerToAnswersChange"
    | "onConditionClauseChange"
    | "onAddConditionClause"
    | "onRemoveConditionClause"
    | "onOperatorChange"
> & {
    workflowQuestion: WorkflowQuestionWithClientId;
    section: {
        title: string;
        addLabel: string;
        emptyText: string;
        phase: WorkflowTriggerPhase;
        onAdd?: (clientId: string) => void;
        hideActionControls?: boolean;
        droppablePrefix: string;
    };
    getAnswersForQuestion: (questionId: number, selectedAnswerIds?: number[]) => AnswerValue[];
};
